/**
 * Global Channel Registry
 * Sistema de registro dinâmico de canais para Chatwoot
 * Permite que plugins registrem novos tipos de canais sem modificar o core
 */

import { ref } from 'vue';

class ChannelRegistry extends EventTarget {
  constructor() {
    super();
    this.channels = new Map();
    this.coreRegistered = false;
    this._versionRef = ref(0); // Ref reativo para Vue
  }

  /**
   * Registra um novo canal
   * @param {Object} config - Configuração do canal
   * @param {string} config.key - Identificador único (ex: 'quepasa', 'sip')
   * @param {string} config.type - Tipo backend (ex: 'Channel::Quepasa')
   * @param {Component} config.component - Componente Vue de configuração
   * @param {string} config.icon - Ícone (ex: 'i-ri-whatsapp-fill')
   * @param {string} config.title - Título do canal
   * @param {string} config.description - Descrição
   * @param {Object} config.metadata - Metadados extras
   * @returns {boolean} - true se registrado com sucesso
   */
  register(config) {
    const { key, type, component, icon, title, description, metadata = {} } = config;

    if (!key) {
      console.error('[ChannelRegistry] Registration failed: key is required');
      return false;
    }

    if (this.channels.has(key)) {
      console.warn(`[ChannelRegistry] Channel "${key}" already registered, skipping`);
      return false;
    }

    const channelConfig = {
      key,
      type,
      component,
      icon,
      title,
      description,
      metadata,
      registeredAt: new Date().toISOString(),
      source: metadata.core ? 'core' : 'plugin',
    };

    this.channels.set(key, channelConfig);
    this._versionRef.value++; // Incrementa versão reativa

    // Dispatch evento para componentes reativos
    this.dispatchEvent(new CustomEvent('channel:registered', {
      detail: { key, config: channelConfig }
    }));

    return true;
  }

  unregister(key) {
    if (!this.channels.has(key)) {
      console.warn(`[ChannelRegistry] Channel "${key}" not found`);
      return false;
    }

    this.channels.delete(key);
    this._versionRef.value++; // Incrementa versão reativa
    console.log(`[ChannelRegistry] Unregistered channel "${key}"`);

    this.dispatchEvent(new CustomEvent('channel:unregistered', {
      detail: { key }
    }));

    return true;
  }

  getAll() {
    // Acessa _versionRef.value para triggerar Vue computed reactivity
    const version = this._versionRef.value;
    return Array.from(this.channels.values());
  }

  get version() {
    return this._versionRef.value;
  }

  getByKey(key) {
    return this.channels.get(key);
  }

  getByType(type) {
    return Array.from(this.channels.values()).find(c => c.type === type);
  }

  getComponent(key) {
    return this.channels.get(key)?.component;
  }

  getIconByType(type, style = 'fill') {
    const channel = this.getByType(type);
    if (!channel?.icon) return `i-ri-chat-1-${style}`;

    if (channel.icon.endsWith('-fill') || channel.icon.endsWith('-line')) {
      return style === 'line'
        ? channel.icon.replace('-fill', '-line')
        : channel.icon.replace('-line', '-fill');
    }

    return channel.icon;
  }

  getTitleByType(type) {
    return this.getByType(type)?.title || 'Unknown Channel';
  }

  has(key) {
    return this.channels.has(key);
  }

  count() {
    return this.channels.size;
  }

  getCoreChannels() {
    return this.getAll().filter(c => c.metadata.core);
  }

  getPluginChannels() {
    return this.getAll().filter(c => !c.metadata.core);
  }
}

// Singleton global
export const channelRegistry = new ChannelRegistry();

// Expor no window para acesso global (plugins)
if (typeof window !== 'undefined') {
  window.chatwoot = window.chatwoot || {};
  window.chatwoot.channelRegistry = channelRegistry;
  console.log('[ChannelRegistry] Global registry initialized at window.chatwoot.channelRegistry');
  // Plugin channels are registered by each plugin via channelRegistry.register()
  // Core channels are NOT registered here — they come from the original ChannelList.vue
}
